import React, { FC, useMemo, useState } from 'react';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { Box } from '@mui/material';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import { NodeActionType, NodeDTO } from '@store/services/nodes/types';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import {
  getTargetScopeAddPlan,
  getTargetScopeEligibility,
  NODE_ACTIONS,
} from '@utils/nodeActions';

import { AddScopeTree } from './AddScopeTree';
import { NodeActionConfirmModal } from './NodeActionConfirmModal';

type Props = {
  open: boolean;
  onClose: () => void;
  nodeIds: string[];
  mode: Modes;
};

export const AddScopeModal: FC<Props> = ({ open, onClose, nodeIds, mode }) => {
  const { getNodeById } = useStore();
  const projectId = useProjectId();
  const { data: domains = [] } = useGetProjectDomainsQuery(projectId, {
    skip: !projectId,
  });
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction] = useUpdateNodesActionMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const config = NODE_ACTIONS[NodeActionType.AddTargetScope]!;

  const nodes = useMemo(
    () =>
      nodeIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    [nodeIds, getNodeById],
  );
  const plan = useMemo(
    () => getTargetScopeAddPlan(nodes, mode, selected),
    [nodes, mode, selected],
  );
  const eligibleIds = useMemo(
    () =>
      nodes
        .filter((n) => getTargetScopeEligibility(n, mode).eligible)
        .map((n) => n.id),
    [nodes, mode],
  );

  const close = () => {
    setConfirming(false);
    setSelected([]);
    onClose();
  };

  const apply = async () => {
    const response = await updateNodesAction({
      nodeIds: eligibleIds,
      actionType: NodeActionType.AddTargetScope,
      payload: { domainIds: selected },
      mode,
      assetId: selectedAssetId || undefined,
    }).unwrap();
    saveResponseToRedux(response);
    showToast(config.successMessage, 'success');
    close();
  };

  const handleAdd = async () => {
    if (plan.willNotAdd.length === 0) {
      await apply();
    } else {
      setConfirming(true);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={open && !confirming}
        onClose={close}
        onBack={close}
        onConfirm={handleAdd}
        disabled={selected.length === 0}
        title={config.label}
        confirmName={config.menuConfirmName}
        backName="Cancel"
      >
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <AddScopeTree
            domains={domains}
            selected={selected}
            onChange={setSelected}
          />
        </Box>
      </ConfirmDialog>

      <NodeActionConfirmModal
        open={open && confirming}
        title={config.menuLabel}
        text={config.menuText}
        toChange={plan.willAdd}
        toChangeTitle={config.menuToChangeTitle}
        skipped={plan.willNotAdd}
        toSkipTitle={config.menuToSkipTitle}
        confirmName={config.menuConfirmName}
        onBack={close}
        onConfirm={apply}
      />
    </>
  );
};
